import { Sparkles } from 'lucide-react';
import { useSiteMode } from '../../app/siteModeContext';
import { t } from '../../lib/copy';
import { COUNTDOWN_DAYS } from '../../lib/siteConfig';
import { CountdownTiles, describeRemaining } from './FlipTile';
import { SiteShell } from './SiteShell';
import { countdownProgress, useCountdown } from './useCountdown';

const PACIFIC = 'America/Los_Angeles';

/** "Sunday 1 November" and "12:00 am" in Seattle time, whatever the viewer's timezone. */
export function formatOpening(at: Date): { date: string; time: string } {
  const date = new Intl.DateTimeFormat('en-GB', {
    timeZone: PACIFIC,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(at);
  const time = new Intl.DateTimeFormat('en-US', {
    timeZone: PACIFIC,
    hour: 'numeric',
    minute: '2-digit',
  })
    .format(at)
    .toLowerCase();
  return { date, time };
}

/** Launch countdown (D32). Stays up at zero until Remote Config flips the site to live. */
export function CountdownPage() {
  const { config } = useSiteMode();
  const remaining = useCountdown(config.launchAt);
  const progress = countdownProgress(config.launchAt, remaining.now, COUNTDOWN_DAYS);

  return (
    <SiteShell title={t('site.countdown.eyebrow')} progress={progress}>
      <p className="text-sm font-semibold tracking-wide text-valley-700 uppercase">
        {t('site.countdown.eyebrow')}
      </p>
      {remaining.done ? (
        <>
          <span className="mt-6 flex size-16 animate-breathe items-center justify-center rounded-full bg-sunrise-50 text-sunrise-700 shadow-card motion-reduce:animate-none">
            <Sparkles className="size-8" aria-hidden />
          </span>
          <h1 className="mt-5 text-display font-bold text-ink">{t('site.done.title')}</h1>
          <p className="mt-3 max-w-lg text-body text-slate">{t('site.done.body')}</p>
        </>
      ) : (
        <>
          <h1 className="mt-3 text-display font-bold text-ink md:text-4xl">
            {t('site.countdown.title')}
          </h1>
          <p className="mt-3 max-w-lg text-body text-slate">{t('site.countdown.body')}</p>
          <div className="mt-8 w-full max-w-md">
            <CountdownTiles remaining={remaining} />
          </div>
          <p className="sr-only" aria-live="polite">
            {t('site.countdown.remaining', { time: describeRemaining(remaining) })}
          </p>
        </>
      )}
      <p className="mt-6 rounded-full bg-white/80 px-4 py-2 text-sm font-medium text-brand shadow-card backdrop-blur">
        {t('site.countdown.opens', formatOpening(config.launchAt))}
      </p>
    </SiteShell>
  );
}
